import { useTheme } from '@/src/context/ThemeContext';
import { tournamentRepository, updateTournamentUseCase } from '@/src/core/dependencies';
import { ModerationError } from '@/src/domain/models/Moderation';
import { useTranslation } from '@/src/i18n';
import { EditTournamentScreen } from '@/src/presentation/screens/EditTournamentScreen';
import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import { useEffect, useState } from 'react';
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import { platformAlert } from '@/src/core/utils/platformAlert';
import { logger } from '@/src/core/utils';

export default function EditTournamentContainer() {
  const router = useRouter();
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { id } = useLocalSearchParams<{ id: string }>();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [maxParticipants, setMaxParticipants] = useState<number | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadTournament();
  }, [id]);

  const loadTournament = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const tournament = await tournamentRepository.getById(id);
      if (tournament) {
        setName(tournament.name);
        setDescription(tournament.description || '');
        setMaxParticipants(tournament.maxParticipants);
      }
    } catch (error) {
      logger.error('EditTournament', 'Failed to load tournament', { error });
      platformAlert(t('common.error'), t('tournaments.failedToLoad'));
      router.back();
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!id) return;
    if (!name.trim()) {
      platformAlert(t('common.error'), t('alerts.enterTournamentName'));
      return;
    }
    try {
      setSaving(true);
      await updateTournamentUseCase.execute(id, {
        name: name.trim(),
        description: description.trim() || undefined,
        maxParticipants: maxParticipants,
      });
      platformAlert(t('common.success'), t('tournaments.tournamentUpdated'), [{ text: t('common.ok'), onPress: () => router.back() }]);
    } catch (error: any) {
      if (error instanceof ModerationError) {
        platformAlert(t('common.error'), t('moderation.contentBlocked'));
      } else {
        logger.error('EditTournament', 'Failed to update tournament', { error });
        platformAlert(t('common.error'), error.message || t('tournaments.failedToUpdate'));
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <View style={[styles.center, { backgroundColor: colors.background }]}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  return (
    <>
      <Stack.Screen options={{ title: t('tournaments.editTournament') }} />
      <EditTournamentScreen
        name={name}
        setName={setName}
        description={description}
        setDescription={setDescription}
        maxParticipants={maxParticipants}
        setMaxParticipants={setMaxParticipants}
        saving={saving}
        handleSave={handleSave}
        handleCancel={() => router.back()}
        colors={colors}
      />
    </>
  );
}

const styles = StyleSheet.create({
  center: { flex: 1, justifyContent: 'center', alignItems: 'center' },
});